import React, { useState, useCallback } from 'react';
import { Checkbox, Flex, Heading, Popover } from '@amzn/storm-ui';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import InventoryView from '../InventoryView/InventoryView';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import { CustomButtonColor } from '../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import {
  CustomSnackbar,
  SnackbarVariants,
} from '../../../UiComponentsLibrary/CustomSnackbar';
import useApiClient from '../../../Shared/services/api';
import { useAppContext } from '../../../Shared/store/appContext';
import { exportInventoryToExcel } from '../../services/exportService';
import { SLOT_CONFIGS } from '../../constants/slotRegistry';
import { SlotSelectionPanel, SlotCheckboxList } from './Dashboard.styles';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const api = useApiClient();
  const { state } = useAppContext();
  const [isSlotSelectorOpen, setIsSlotSelectorOpen] = useState(false);
  const [selectedSlotKeys, setSelectedSlotKeys] = useState<string[]>(
    SLOT_CONFIGS.map((c) => c.key)
  );

  const artEventId = state.selectedArtEventId;
  const allSelected = selectedSlotKeys.length === SLOT_CONFIGS.length;

  const { mutate: doExport, isLoading, isError, isSuccess, reset } = useMutation({
    mutationFn: (slotKeys?: string[]) =>
      exportInventoryToExcel(api, artEventId, slotKeys),
  });

  const handleSlotToggle = useCallback((key: string) => {
    setSelectedSlotKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedSlotKeys(
      allSelected ? [] : SLOT_CONFIGS.map((c) => c.key)
    );
  }, [allSelected]);

  const handleExport = () => {
    setIsSlotSelectorOpen(false);
    const slotKeys = allSelected ? undefined : selectedSlotKeys;
    doExport(slotKeys);
  };

  const slotSelectorTrigger = (
    <CustomButton
      id="export-inventory-btn"
      text={isLoading ? t('exporting') : t('export-inventory')}
      backgroundColor={CustomButtonColor.submit}
      handleOnClickCb={() => setIsSlotSelectorOpen((prev) => !prev)}
      disabled={isLoading || !artEventId}
    />
  );

  return (
    <>
      <CustomSnackbar
        id="inventory-export-error-snackbar"
        variant={SnackbarVariants.ERROR}
        message={t('inventory-export-error')}
        isVisible={isError}
        onClose={reset}
      />
      <CustomSnackbar
        id="inventory-export-success-snackbar"
        variant={SnackbarVariants.SUCCESS}
        message={t('inventory-export-success')}
        isVisible={isSuccess}
        onClose={reset}
      />
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="medium">{t('dashboard-header')}</Heading>
        <Popover
          id="slot-selection-popover"
          isOpen={isSlotSelectorOpen}
          onCloseButtonClick={() => setIsSlotSelectorOpen(false)}
          position="bottom"
          align="end"
          trigger={slotSelectorTrigger}
        >
          <SlotSelectionPanel>
            <SlotCheckboxList>
              <Checkbox
                id="select-all-slots-checkbox"
                checked={allSelected}
                label={t('select-all-slots')}
                onChange={handleSelectAll}
              />
              {SLOT_CONFIGS.map((config) => (
                <Checkbox
                  key={config.key}
                  id={`slot-checkbox-${config.key}`}
                  checked={selectedSlotKeys.includes(config.key)}
                  label={config.sheetName}
                  onChange={() => handleSlotToggle(config.key)}
                />
              ))}
            </SlotCheckboxList>
            <CustomButton
              id="export-selected-slots-btn"
              text={t('export-selected')}
              backgroundColor={CustomButtonColor.submit}
              handleOnClickCb={handleExport}
              disabled={selectedSlotKeys.length === 0 || isLoading}
            />
          </SlotSelectionPanel>
        </Popover>
      </Flex>
      <InventoryView />
    </>
  );
};

export default Dashboard;
